/**
 * `<StaleContentNotice>` — the GM's warning that a world sidecar copy is out of date with the repo's
 * authored pack, so the rules it carries may not be running (`backend/src/world/sidecarStaleness.ts`).
 *
 * The case it exists for: a session-one copy staged before a trigger rename lost Terraforming Treads
 * at the schema gate, and the rig silently stopped bulldozing. The server already logs this at boot;
 * this puts it where the GM is looking. GM-only because the fix is a server-side restage only the
 * GM can run. Renders nothing while the fetch is pending, when it fails, or when every copy is current.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchStaleWorldSidecars } from "../../lib/api";

export function StaleContentNotice(): React.JSX.Element | null {
  const { data } = useQuery({ queryKey: ["world-sidecars-stale"], queryFn: fetchStaleWorldSidecars, staleTime: Infinity });
  if (!data || data.length === 0) return null;
  return (
    <aside className="combat-stale-content" role="alert" aria-label="Stale world content">
      <span className="combat-stale-content-title">CONTENT OUT OF DATE</span>
      <span>
        {data.map((s) => s.name).join(", ")}: this world's copy differs from the pack, so some of its rules may not run. Stop the
        server, run from the code checkout, and restart:
      </span>
      {data.map((s) => (
        <code key={s.name} className="combat-stale-content-command">
          {s.restage}
        </code>
      ))}
    </aside>
  );
}
